/* ─────────────────────────────────────────────────────────────────────────
   dashboardV3 — shared styled shells
   These are COPIED from DashboardV2 (summary strip, OuterBox, SensorType) plus
   a few new presentational pieces (category tiles, filter chips, page header).
   Copied — not imported — so the current dashboard code is never touched.

   Mobile (max480) is tuned to be compact: the summary strip with the three
   gateway items must fit a 360–375px screen without wrapping or scrolling.
   ──────────────────────────────────────────────────────────────────────── */
import styled from "styled-components";
import { media } from "../../design/breakpoints";

/* Page shell — mirrors DashboardV2 Container */
export const PageContainer = styled.div`
  margin: auto;
  width: 90%;
  position: relative;
  margin-top: 0;
  ${media.max480} { width: 92%; }
`;

export const ContentContainer = styled.div`
  display: grid;
  grid-template-columns: minmax(0, min(max-content, 100%));
  justify-content: center;
  row-gap: 0.3rem;
  width: 100%;
  ${media.max480} { row-gap: 0.2rem; }
`;

/* ── Summary strip ────────────────────────────────────────────────────────
   Desktop: shrinks to content.
   Mobile : full width, items share the space evenly so 6 items always fit. */
export const SummaryStrip = styled.div`
  display: flex;
  align-items: stretch;
  background: #fff;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  overflow: visible;
  margin: 0.4rem auto 0.6rem;
  width: fit-content;
  max-width: 100%;
  ${media.max480} {
    width: 100%;
    margin: 0.3rem auto 0.4rem;
    border-radius: 10px;
  }
`;

export const SummaryItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4px 10px;
  border-right: 1px solid #f3f4f6;
  &:last-child { border-right: none; }
  ${media.max480} {
    flex: 1 1 0;
    min-width: 0;
    padding: 4px 3px;
  }
`;

export const SummaryCount = styled.span`
  font-size: 1.1rem;
  font-weight: 700;
  color: ${({ $color }) => $color || "#111827"};
  line-height: 1;
  ${media.max480} { font-size: 0.95rem; }
`;

export const SummaryLabel = styled.span`
  font-size: 0.6rem;
  font-weight: 600;
  color: #4b5563;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  margin-top: 2px;
  white-space: nowrap;
  ${media.max480} {
    font-size: 0.5rem;
    letter-spacing: 0.02em;
    margin-top: 1px;
  }
`;

/* ── OuterBox + section heading (copied from DashboardV2) ─────────────────── */
export const OuterBox = styled.div`
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  background: #fafafa;
  border-radius: 14px;
  padding: 0.4rem 0.3rem 0.7rem;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
  display: flex;
  flex-wrap: wrap;
  column-gap: 20px;
  row-gap: 4px;
  justify-content: center;
  ${media.max480} {
    padding: 0.3rem 0.2rem 0.5rem;
    column-gap: 12px;
    row-gap: 3px;
  }
`;

export const SensorType = styled.h3`
  color: #107066;
  font-size: 1rem;
  font-weight: 700;
  margin: 0.15rem 0 0.2rem 0.2rem;
  padding: 0;
  width: 100%;
  text-align: left;
  display: flex;
  align-items: center;
  gap: 7px;
  ${media.max480} {
    font-size: 0.92rem;
    margin: 0.1rem 0 0.1rem 0.15rem;
  }
`;

export const AlertBadge = styled.span`
  background: rgba(245, 158, 11, 0.15);
  color: #b45309;
  font-size: 11px;
  font-weight: 600;
  padding: 2px 9px;
  border-radius: 10px;
  margin-left: 6px;
  ${media.max480} {
    font-size: 10px;
    padding: 1px 7px;
    margin-left: 4px;
  }
`;

/* Cards wrap centred, same as DashboardV2 DeviceGroup */
export const DeviceGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  width: 100%;
`;

/* ── NEW: category tiles for the hub ─────────────────────────────────────── */
export const TileGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 12px;
  width: 100%;
  max-width: 640px;
  margin: 0.6rem auto 0;
  ${media.max480} {
    grid-template-columns: 1fr 1fr;
    gap: 8px;
    margin-top: 0.4rem;
  }
`;

export const Tile = styled.button`
  text-align: left;
  border: none;
  background: #fafafa;
  border-radius: 14px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
  padding: 13px 15px;
  cursor: pointer;
  font-family: inherit;
  transition: all 0.2s ease;
  &:hover { box-shadow: 0 4px 14px rgba(35, 122, 53, 0.15); transform: translateY(-1px); }
  ${media.max480} {
    padding: 9px 10px;
    border-radius: 12px;
  }
`;

export const TileHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  color: #107066;
  font-weight: 800;
  font-size: 1.02rem;
  overflow: hidden;
  /* keep the label (icon + text) on a single line */
  & > span { white-space: nowrap; min-width: 0; }
  ${media.max480} { font-size: 0.88rem; gap: 4px; }
`;

/* Count line: number + unit on ONE line, same height, smaller than the heading */
export const TileCount = styled.div`
  font-size: 0.95rem;
  font-weight: 700;
  color: #111827;
  margin-top: 7px;
  b { font-weight: 800; }
  ${media.max480} { font-size: 0.84rem; margin-top: 4px; }
`;

export const TileSub = styled.div`
  font-size: 0.82rem;
  font-weight: 600;
  color: #4b5563;
  margin-top: 4px;
  line-height: 1.5;
  ${media.max480} { font-size: 0.72rem; margin-top: 2px; line-height: 1.35; }
`;

/* Second status line: the message / error, shown under online·offline */
export const TileMsg = styled.div`
  font-size: 0.82rem;
  font-weight: 600;
  color: #4b5563;
  margin-top: 3px;
  line-height: 1.45;
  ${media.max480} { font-size: 0.72rem; margin-top: 1px; line-height: 1.35; }
`;

/* ── NEW: page header (back + title) for category pages ──────────────────── */
export const PageHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 9px;
  margin: 0.2rem 0 0.6rem;
  padding: 0 0.2rem;
  ${media.max480} { gap: 7px; margin: 0.1rem 0 0.4rem; }
`;

/* Matches the back button on the device detail pages (SensorDetailV2 etc.):
   circular white button with a plain ← glyph */
export const BackBtn = styled.button`
  width: 36px; height: 36px;
  border-radius: 50%;
  border: 1.5px solid #e5e7eb;
  background: #fff;
  display: flex; align-items: center; justify-content: center;
  font-size: 18px;
  cursor: pointer;
  color: #374151;
  flex-shrink: 0;
  &:hover { border-color: #237a35; color: #237a35; }
  ${media.max480} { width: 32px; height: 32px; font-size: 16px; }
`;

export const HeaderTitle = styled.span`
  font-size: 1.15rem;
  font-weight: 700;
  color: #111827;
  ${media.max480} { font-size: 1.02rem; }
`;

export const HeaderCount = styled.span`
  margin-left: auto;
  font-size: 0.8rem;
  color: #4b5563;
  ${media.max480} { font-size: 0.74rem; }
`;

/* ── NEW: sort / filter chips ────────────────────────────────────────────── */
export const ChipRow = styled.div`
  display: flex;
  gap: 8px;
  overflow-x: auto;
  margin-bottom: 0.5rem;
  padding: 0 0.2rem 2px;
  ${media.max480} { gap: 6px; margin-bottom: 0.35rem; }
`;

export const Chip = styled.button`
  font-size: 12.5px;
  font-weight: 500;
  padding: 6px 13px;
  border-radius: 20px;
  white-space: nowrap;
  cursor: pointer;
  font-family: inherit;
  border: 1px solid ${({ $active }) => ($active ? "#237A35" : "#e5e7eb")};
  background: ${({ $active }) => ($active ? "#237A35" : "#fff")};
  color: ${({ $active }) => ($active ? "#fff" : "#4b5563")};
  transition: all 0.15s ease;
  ${media.max480} { font-size: 11.5px; padding: 4px 10px; }
`;

/* ── NEW: Needs-attention alert rows (details, not full cards) ───────────── */
export const AlertCard = styled.div`
  background: #fff;
  border: 1px solid #f0e3c8;
  border-radius: 14px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  margin: 0.2rem auto 0;
  width: 100%;
  max-width: 640px;
  ${media.max480} { border-radius: 12px; }
`;

export const AlertHead = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 11px 14px;
  border-bottom: 1px solid #f3f4f6;
  color: #b45309;
  font-weight: 700;
  font-size: 0.9rem;
  ${media.max480} { padding: 8px 10px; gap: 6px; font-size: 0.82rem; }
`;

export const AlertItem = styled.button`
  display: flex;
  align-items: center;
  gap: 11px;
  width: 100%;
  border: none;
  background: none;
  text-align: left;
  padding: 11px 14px;
  cursor: pointer;
  font-family: inherit;
  border-bottom: 1px solid #f3f4f6;
  transition: background 0.15s ease;
  &:last-child { border-bottom: none; }
  &:hover { background: #fafafa; }
  ${media.max480} { gap: 8px; padding: 7px 10px; }
`;

export const AlertDot = styled.span`
  width: 9px;
  height: 9px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${({ $color }) => $color || "#9CA3AF"};
  ${media.max480} { width: 7px; height: 7px; }
`;

export const AlertText = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 4px 8px;
  ${media.max480} { gap: 0 6px; }
`;

export const AlertName = styled.span`
  font-size: 0.9rem;
  font-weight: 600;
  color: #111827;
  ${media.max480} { font-size: 0.82rem; }
`;

export const AlertReason = styled.span`
  font-size: 0.78rem;
  color: #6b7280;
  ${media.max480} { font-size: 0.7rem; }
`;

export const AlertSince = styled.span`
  font-size: 0.72rem;
  font-weight: 500;
  color: #d81111;
  white-space: nowrap;
  flex-shrink: 0;
  ${media.max480} { font-size: 0.65rem; }
`;

/* Empty / loading messages */
export const Msg = styled.div`
  text-align: center;
  color: #6b7280;
  font-size: 0.85rem;
  padding: 1.5rem 0;
  ${media.max480} { font-size: 0.8rem; padding: 1rem 0; }
`;

/* Summary item with an instant hover tooltip over the whole box */
export const TipItem = styled(SummaryItem)`
  position: relative;
  cursor: ${({ $hasTip }) => ($hasTip ? "pointer" : "default")};
  &:last-child { border-radius: 0 11px 11px 0; }
  &:hover { background: ${({ $hasTip }) => ($hasTip ? "#f9fafb" : "transparent")}; }
`;

/* Compact hover tooltip; background color is passed in via $bg (red / amber / green) */
export const Tip = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 4px;
  z-index: 20;
  background: ${({ $bg }) => $bg || "#111827"};
  color: #fff;
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1.35;
  padding: 6px 9px;
  border-radius: 6px;
  white-space: nowrap;
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.18);
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  transition: opacity 0.15s ease;
  & > div + div { margin-top: 2px; }
  ${TipItem}:hover & { opacity: 1; visibility: visible; }
`;